"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/src/lib/utils";
import { Text } from "@/src/components/ui/Typography";
import type { Heading } from "@/src/lib/blog/toc";

const ACTIVE_OFFSET_PX = 140; // matches fixed site-header height + a little breathing room
const SCROLL_LOCK_MS = 800; // how long to freeze the active-tracker after a click-to-scroll

interface TableOfContentsProps {
  headings: Heading[];
}

export function TableOfContents({ headings }: TableOfContentsProps) {
  const items: Heading[] = useMemo(() => headings, [headings]);

  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
  const lockUntilRef = useRef<number>(0);
  const listRef = useRef<HTMLUListElement | null>(null);
  const itemRefs = useRef<Map<string, HTMLAnchorElement>>(new Map());

  useEffect(() => {
    if (items.length === 0) return;

    const updateActive = () => {
      if (Date.now() < lockUntilRef.current) return;
      let current = items[0].id;
      for (const h of items) {
        const el = document.getElementById(h.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - ACTIVE_OFFSET_PX <= 0) {
          current = h.id;
        } else {
          break;
        }
      }
      setActiveId(current);
    };

    updateActive();
    window.addEventListener("scroll", updateActive, { passive: true });
    window.addEventListener("resize", updateActive);
    return () => {
      window.removeEventListener("scroll", updateActive);
      window.removeEventListener("resize", updateActive);
    };
  }, [items]);

  // Keep the active entry visible inside the panel's own scroll area — scoped
  // to this container only (via scrollTop math) so it never nudges the
  // page's own scroll position.
  useEffect(() => {
    if (!activeId) return;
    const container = listRef.current;
    const el = itemRefs.current.get(activeId);
    if (!container || !el) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    if (elRect.top < containerRect.top) {
      container.scrollTop -= containerRect.top - elRect.top + 8;
    } else if (elRect.bottom > containerRect.bottom) {
      container.scrollTop += elRect.bottom - containerRect.bottom + 8;
    }
  }, [activeId]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      // Resolve the heading element. Prefer the exact id, but fall back to a
      // case-insensitive lookup in case a slug mismatch slipped through.
      let el = document.getElementById(id);
      if (!el) {
        const all = Array.from(
          document.querySelectorAll<HTMLElement>("h2[id], h3[id]"),
        );
        el = all.find((n) => n.id.toLowerCase() === id.toLowerCase()) ?? null;
      }
      if (!el) return; // let the native href handle it if the anchor truly doesn't exist
      e.preventDefault();
      // Freeze the scroll tracker so it doesn't paint intermediate headings active
      lockUntilRef.current = Date.now() + SCROLL_LOCK_MS;
      setActiveId(id);
      window.history.pushState(null, "", `#${id}`);
      const top =
        el.getBoundingClientRect().top +
        window.scrollY -
        (ACTIVE_OFFSET_PX - 20);
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    },
    [],
  );

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="On this page"
      className="hidden self-start lg:block"
      style={{ position: "sticky", top: "6rem" }}
    >
      <div className="flex max-h-[calc(100vh-10rem)] flex-col rounded-sm border border-neutral-100 bg-neutral-50">
        <div className="shrink-0 border-b border-neutral-100 px-4 py-3">
          <Text as="p" size="bodySMMedium" className="text-neutral-500">
            On this page
          </Text>
        </div>
        <ul
          ref={listRef}
          className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-200 [&::-webkit-scrollbar-thumb]:hover:bg-neutral-300"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "var(--color-neutral-200) transparent",
          }}
        >
          {items.map((h) => (
            <li key={h.id}>
              <a
                ref={(el) => {
                  if (el) itemRefs.current.set(h.id, el);
                  else itemRefs.current.delete(h.id);
                }}
                href={`#${h.id}`}
                onClick={(e) => handleClick(e, h.id)}
                aria-current={activeId === h.id ? "location" : undefined}
                className={cn(h.depth === 3 && "block pl-4")}
              >
                <Text
                  as="span"
                  size="bodySM"
                  className={cn(
                    "transition font-light",
                    activeId === h.id
                      ? "text-brand"
                      : "text-neutral-600 hover:text-brand",
                  )}
                >
                  {h.text}
                </Text>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
